import { useClientModal } from '.'
import { toast } from '@utils/functions/notificationToast'

import { parseDistributeBillingPayload } from '../helpers/parseDistributeBillingPayload'
import { parseCreateNewServicePayload } from '../helpers/parseCreateNewServicePayload'
import { parseAddEditOtherServicePayload } from '../helpers/parseAddEditOtherServicePayload'
import { useInvoicing } from '@pages/Invoicing/context'

import type { AxiosError } from 'axios'
import type {
  GetLastDistributionValuesRequestProps,
  GetOtherServicesRequestProps,
  GetServiceDescriptionsRequestProps,
  GetServiceGroupingRequestProps,
  GetWeeklyBreakdownRequestProps,
  RemoveOtherServiceRequestProps,
  RemoveSelectedServicesRequestProps,
  RemoveServiceRequestProps,
  ResetClientRequestProps
} from '@services/api/invoicing/clients/types'
import type { DistributeBillingModalFormProps } from '../components/DistributeBillingModal/form/types'
import type { AddEditServiceModalFormProps } from '../components/AddEditServiceModal/form/types'
import type { AddEditOtherServicesModalFormProps } from '@pages/Invoicing/components/AddEditOtherServicesModal/form/types'

import * as apiInvoicingClients from '@services/api/invoicing/clients'

const getValidationErrorsMessage = (errors: Record<string, string[]>) => {
  const firstKey = Object.keys(errors)[0]
  const messages = firstKey ? errors[firstKey] : []
  return Array.isArray(messages) && messages.length > 0 ? messages[0] : null
}

const getApiErrorMessage = (error: unknown, fallbackMessage: string) => {
  const axiosError = error as AxiosError<{
    errorMessage?: string[]
    message?: string
    errors?: Record<string, string[]>
  }>
  const data = axiosError?.response?.data
  if (data && typeof data === 'object') {
    const fromErrors = data.errors && getValidationErrorsMessage(data.errors)
    return (
      fromErrors ||
      data.errorMessage?.[0] ||
      data.message ||
      fallbackMessage
    )
  }
  return fallbackMessage
}

const removeOtherService = async (payload: RemoveOtherServiceRequestProps) => {
  const { setIsRemoveOtherServiceLoading } = useClientModal.getState()
  const {
    getClients,
    getPreviewDetails,
    getDetailedSummary,
    getWeeklybreakdown: getWeeklybreakdownCl,
    clientsData
  } = useInvoicing.getState()

  setIsRemoveOtherServiceLoading(true)

  try {
    await apiInvoicingClients.removeOtherService(payload)

    toast.success('O outro serviço foi excluído com sucesso!')

    getOtherServices({
      competenceDate: payload.fatDtCompetencia,
      filiCode: payload.filiCode,
      clientCode: payload.clientCode,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: payload.filiCode,
      fatDtCompetencia: payload.fatDtCompetencia,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia
    })

    getDetailedSummary({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia
    })

    getWeeklybreakdownCl({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao excluir o outro serviço!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsRemoveOtherServiceLoading(false)
  }
}

const editOtherService = async (formData: AddEditOtherServicesModalFormProps) => {
  const { setIsCreateNewOtherServiceLoading } = useClientModal.getState()
  const {
    getClients,
    getPreviewDetails,
    getDetailedSummary,
    getWeeklybreakdown: getWeeklybreakdownCl,
    clientsData
  } = useInvoicing.getState()

  setIsCreateNewOtherServiceLoading(true)

  try {
    const payload = parseAddEditOtherServicePayload(formData)

    await apiInvoicingClients.editOtherService(payload)

    toast.success('O outro serviço foi editado com sucesso!')

    getOtherServices({
      filiCode: formData.filiCode,
      clientCode: formData.clientCode,
      competenceDate: formData.competenceDate,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: formData.filiCode,
      fatDtCompetencia: formData.competenceDate,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getDetailedSummary({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getWeeklybreakdownCl({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao editar um outro serviço!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsCreateNewOtherServiceLoading(false)
  }
}

const createNewOtherService = async (formData: AddEditOtherServicesModalFormProps) => {
  const { setIsCreateNewOtherServiceLoading } = useClientModal.getState()
  const {
    getClients,
    getPreviewDetails,
    getDetailedSummary,
    getWeeklybreakdown: getWeeklybreakdownCl,
    clientsData
  } = useInvoicing.getState()

  setIsCreateNewOtherServiceLoading(true)

  try {
    const payload = parseAddEditOtherServicePayload(formData)

    await apiInvoicingClients.createNewOtherService(payload)

    toast.success('O outro serviço foi adicionado com sucesso!')

    getOtherServices({
      filiCode: formData.filiCode,
      clientCode: formData.clientCode,
      competenceDate: formData.competenceDate,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: formData.filiCode,
      fatDtCompetencia: formData.competenceDate,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getDetailedSummary({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate
    })

    getWeeklybreakdownCl({
      filiCode: formData.filiCode,
      competenceDate: formData.competenceDate,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao criar um outro serviço!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsCreateNewOtherServiceLoading(false)
  }
}

const getOtherServices = async (payload: GetOtherServicesRequestProps) => {
  const { setOtherServicesData, setIsOtherServicesLoading } = useClientModal.getState()

  setIsOtherServicesLoading(true)

  try {
    const data = await apiInvoicingClients.getOtherServices(payload)

    setOtherServicesData(data)
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao buscar os outros serviços!')
    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsOtherServicesLoading(false)
  }
}

const removeService = async (payload: RemoveServiceRequestProps) => {
  const { setIsRemoveServiceLoading } = useClientModal.getState()
  const { getClients, getPreviewDetails, getWeeklybreakdown: getWeeklybreakdownCl, clientsData } =
    useInvoicing.getState()

  setIsRemoveServiceLoading(true)

  try {
    await apiInvoicingClients.removeService(payload)

    toast.success('O serviço foi excluído com sucesso!')

    getWeeklyBreakdown({
      cdClienteNl: payload.clientCode!,
      fatDtCompetencia: payload.fatDtCompetencia,
      filiCodigo: payload.filiCode,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: payload.filiCode,
      fatDtCompetencia: payload.fatDtCompetencia,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia
    })

    getWeeklybreakdownCl({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao excluir o serviço!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsRemoveServiceLoading(false)
  }
}

const removeSelectedServices = async (payload: RemoveSelectedServicesRequestProps) => {
  const { setIsRemoveSelectedServicesLoading } = useClientModal.getState()
  const { getClients, getPreviewDetails, getWeeklybreakdown: getWeeklybreakdownCl, clientsData } =
    useInvoicing.getState()

  setIsRemoveSelectedServicesLoading(true)

  try {
    await apiInvoicingClients.removeSelectedServices(payload)

    toast.success('Serviços excluídos com sucesso!')

    getWeeklyBreakdown({
      cdClienteNl: payload.clientCode,
      fatDtCompetencia: payload.fatDtCompetencia,
      filiCodigo: payload.filiCode,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: payload.filiCode,
      fatDtCompetencia: payload.fatDtCompetencia,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia
    })

    getWeeklybreakdownCl({
      filiCode: payload.filiCode,
      competenceDate: payload.fatDtCompetencia,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao excluir os serviços!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsRemoveSelectedServicesLoading(false)
  }
}

const editService = async (formData: AddEditServiceModalFormProps) => {
  const { setIsCreateNewServiceLoading } = useClientModal.getState()
  const { getClients, getPreviewDetails, getWeeklybreakdown: getWeeklybreakdownCl, clientsData } =
    useInvoicing.getState()

  setIsCreateNewServiceLoading(true)

  try {
    const payload = parseCreateNewServicePayload(formData)

    await apiInvoicingClients.editService(payload)

    toast.success('O serviço foi editado com sucesso!')

    getWeeklyBreakdown({
      cdClienteNl: formData.cdClientCode,
      fatDtCompetencia: formData.fatCompetenceDate,
      filiCodigo: formData.filiCode,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: formData.filiCode,
      fatDtCompetencia: formData.fatCompetenceDate,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: formData.filiCode,
      competenceDate: formData.fatCompetenceDate
    })

    getWeeklybreakdownCl({
      filiCode: formData.filiCode,
      competenceDate: formData.fatCompetenceDate,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao editar o serviço!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsCreateNewServiceLoading(false)
  }
}

const createNewService = async (formData: AddEditServiceModalFormProps) => {
  const { setIsCreateNewServiceLoading } = useClientModal.getState()
  const { getClients, getPreviewDetails, getWeeklybreakdown: getWeeklybreakdownCl, clientsData } =
    useInvoicing.getState()

  setIsCreateNewServiceLoading(true)

  try {
    const payload = parseCreateNewServicePayload(formData)

    await apiInvoicingClients.createNewService(payload)

    toast.success('O serviço foi adicionado com sucesso!')

    await getWeeklyBreakdown({
      cdClienteNl: formData.cdClientCode,
      fatDtCompetencia: formData.fatCompetenceDate,
      filiCodigo: formData.filiCode,
      currentPage: 1,
      itemsPerPage: 10
    })

    await getClients({
      filiCodigo: formData.filiCode,
      fatDtCompetencia: formData.fatCompetenceDate,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    await getWeeklybreakdownCl({
      filiCode: formData.filiCode,
      competenceDate: formData.fatCompetenceDate,
      currentPage: 1,
      itemsPerPage: 10
    })

    await getPreviewDetails({
      filiCode: payload.filiCodigo,
      competenceDate: payload.fatDtCompetencia
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao criar o serviço!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsCreateNewServiceLoading(false)
  }
}

type ServiceDescriptionItem = {
  id: number
  description: string
  unitGrossValue: number
  billingTaxPercentage: number
}

const normalizeServiceDescriptionItem = (item: Record<string, unknown>): ServiceDescriptionItem => {
  const desc =
    (item.description as string) ??
    (item.descricao as string) ??
    (item.nmDescricao as string) ??
    (item.nome as string) ??
    ''
  const rawUnit = item.unitGrossValue ?? item.vlBrutoUnitario ?? item.valorUnitario
  const unitGross = rawUnit != null ? Number(rawUnit) : 0
  const rawTax = item.billingTaxPercentage ?? item.contcVlrlFatPerimposto ?? item.percentualImposto
  const tax = rawTax != null ? Number(rawTax) : 0
  return {
    id: item.id != null ? Number(item.id) : 0,
    description: String(desc),
    unitGrossValue: Number.isNaN(unitGross) ? 0 : unitGross,
    billingTaxPercentage: Number.isNaN(tax) ? 0 : tax
  }
}

const getServiceDescriptions = async (payload: GetServiceDescriptionsRequestProps) => {
  const { setServiceDescriptionsData, setIsServiceDescriptionsLoading } = useClientModal.getState()

  setIsServiceDescriptionsLoading(true)

  try {
    const data = await apiInvoicingClients.getServiceDescriptions(payload)

    const items = Array.isArray(data?.items)
      ? data.items.map((item: Record<string, unknown>) => normalizeServiceDescriptionItem(item))
      : []

    setServiceDescriptionsData(items)
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao buscar as descrições dos serviços!')
    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsServiceDescriptionsLoading(false)
  }
}

const getServiceGrouping = async (payload: GetServiceGroupingRequestProps) => {
  const { setServiceGroupingData, setIsServiceGroupingLoading } = useClientModal.getState()

  setIsServiceGroupingLoading(true)

  try {
    const data = await apiInvoicingClients.getServiceGrouping(payload)

    setServiceGroupingData(data.items)
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao buscar os serviços!')
    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsServiceGroupingLoading(false)
  }
}

const resetClient = async (payload: ResetClientRequestProps) => {
  const { setIsResetClientLoading } = useClientModal.getState()
  const { getClients, getPreviewDetails, getDetailedSummary, getWeeklybreakdown: getWeeklybreakdownCl, clientsData } =
    useInvoicing.getState()

  setIsResetClientLoading(true)

  try {
    await apiInvoicingClients.resetClient(payload)

    toast.success('A restauração foi realizada com sucesso!')

    getWeeklyBreakdown({
      cdClienteNl: payload.cdClienteNl,
      fatDtCompetencia: payload.fatDtCompetencia,
      filiCodigo: payload.filiCodigo,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: payload.filiCodigo,
      fatDtCompetencia: payload.fatDtCompetencia,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: payload.filiCodigo,
      competenceDate: payload.fatDtCompetencia
    })

    getDetailedSummary({
      filiCode: payload.filiCodigo,
      competenceDate: payload.fatDtCompetencia
    })

    getWeeklybreakdownCl({
      filiCode: payload.filiCodigo,
      competenceDate: payload.fatDtCompetencia,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao realizar a restauração!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsResetClientLoading(false)
  }
}

const distributeBilling = async (formData: DistributeBillingModalFormProps) => {
  const { setIsDistributeBillingLoading } = useClientModal.getState()
  const { getClients, getPreviewDetails, getDetailedSummary, getWeeklybreakdown: getWeeklybreakdownCl, clientsData } =
    useInvoicing.getState()

  setIsDistributeBillingLoading(true)

  try {
    const payload = parseDistributeBillingPayload(formData)

    await apiInvoicingClients.distributeBilling(payload)

    toast.success('A Distribuição foi realizada com sucesso!!')

    getWeeklyBreakdown({
      cdClienteNl: payload.cdClienteNl,
      fatDtCompetencia: payload.fatDtCompetencia,
      filiCodigo: payload.filiCodigo,
      currentPage: 1,
      itemsPerPage: 10
    })

    getClients({
      filiCodigo: payload.filiCodigo,
      fatDtCompetencia: payload.fatDtCompetencia,
      currentPage: clientsData?.currentPage || 1,
      itemsPerPage: clientsData?.itemsPerPage || 10
    })

    getPreviewDetails({
      filiCode: payload.filiCodigo,
      competenceDate: payload.fatDtCompetencia
    })

    getDetailedSummary({
      filiCode: payload.filiCodigo,
      competenceDate: payload.fatDtCompetencia
    })

    getWeeklybreakdownCl({
      filiCode: payload.filiCodigo,
      competenceDate: payload.fatDtCompetencia,
      currentPage: 1,
      itemsPerPage: 10
    })

    return true
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falhar ao realizar a distribuição!')
    toast.error(errorMessage, (error as AxiosError)?.status)

    return false
  } finally {
    setIsDistributeBillingLoading(false)
  }
}

const getWeeklyBreakdown = async (payload: GetWeeklyBreakdownRequestProps) => {
  const { setWeeklyBreakdownData, setIsWeeklyBreakdownLoading } = useClientModal.getState()

  setIsWeeklyBreakdownLoading(true)

  try {
    const data = await apiInvoicingClients.getWeeklyBreakdown(payload)

    setWeeklyBreakdownData(data)
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao buscar o faturamento semanal!')
    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsWeeklyBreakdownLoading(false)
  }
}

const getLastDistributionValues = async (payload: GetLastDistributionValuesRequestProps) => {
  const { setLastDistributionValuesData, setIsLastDistributionValuesLoading } = useClientModal.getState()

  setIsLastDistributionValuesLoading(true)

  try {
    const data = await apiInvoicingClients.getLastDistributionValues(payload)

    setLastDistributionValuesData(data)
  } catch (error) {
    const errorMessage = getApiErrorMessage(error, 'Falha ao buscar os valores da última distribuição!')
    toast.error(errorMessage, (error as AxiosError)?.status)
  } finally {
    setIsLastDistributionValuesLoading(false)
  }
}

export {
  getWeeklyBreakdown,
  distributeBilling,
  resetClient,
  getLastDistributionValues,
  getServiceGrouping,
  getServiceDescriptions,
  createNewService,
  editService,
  removeService,
  removeSelectedServices,
  getOtherServices,
  createNewOtherService,
  editOtherService,
  removeOtherService
}
